import { useContext } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './contents.css';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { useSaveAppConfigMutation } from '#services/app';
import useForm from '#utils/use-form';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { appConfigValidations } from '#common/validation';
import {
  useData,
} from './Website.utils';

const BlockEmbed = Quill.import('blots/block/embed') as new (...args: unknown[]) => object;
class DividerBlot extends BlockEmbed {}
(DividerBlot as unknown as { blotName: string }).blotName = 'divider';
(DividerBlot as unknown as { tagName: string }).tagName = 'hr';
(Quill.register as (target: unknown, overwrite?: boolean) => void)(DividerBlot);

interface QuillLike {
  getSelection(): { index: number } | null;
  insertEmbed(index: number, type: string, value: boolean, source: string): void;
  setSelection(index: number, source: string): void;
}

const modules = {
  toolbar: {
    container: [
      [{ font: [] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ align: [] }],
      [{ direction: 'rtl' }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      ['blockquote', 'code-block'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      ['hr'],
    ],
    handlers: {
      hr(this: { quill: QuillLike }) {
        const range = this.quill.getSelection();
        if (range) {
          this.quill.insertEmbed(range.index, 'divider', true, 'user');
          this.quill.setSelection(range.index + 1, 'silent');
        }
      },
    },
  },
};

export default function WebsiteContents() {
  const [saveAppConfig] = useSaveAppConfigMutation();

  const {
    showBackdrop,
    hideBackdrop,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const {
    values,
    handleChange,
    handleSubmit,
    addSubmitListener,
    resetForm,
    touched,
  } = useForm({
    initialState: { loading: true },
    validations: appConfigValidations,
  });

  useData({ resetForm });

  const handleSaveContents = (values: Record<string, unknown>, isValid: boolean) => {
    if (!isValid) {
      return;
    }

    // build the diffs
    const changedValues = Object.keys(touched).reduce<Record<string, unknown>>((acc, key) => {
      if (!touched[key]) {
        return acc;
      }

      acc[key] = values[key];
      return acc;
    }, {});

    showBackdrop();
    void saveAppConfig(changedValues)
      .then((res) => {
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], 'Website settings saved successfully.');
        if (hasError) {
          return;
        }

        resetForm({
          ...res.data,
          loading: false,
        });
      }).finally(() => {
        hideBackdrop();
      });
  };
  addSubmitListener(handleSaveContents);

  return (
    <Box
      sx={{
        '& .MuiFormControl-root': { mb: 1.5 },
        '& .MuiPaper-root': { mb: 2 },
      }}
    >
      <Box sx={{ typography: 'h7', pb: 2 }}>
        Announcement
      </Box>

      <ReactQuill
        theme="snow"
        style={{
          paddingBottom: '10px',
        }}
        placeholder="Enter some text..."
        value={values.announcement as string}
        modules={modules}
        onChange={(html) => {
          handleChange(null, {
            name: 'announcement',
            value: html,
          });
        }}
      />

      <Button
        type="button"
        variant="contained"
        onClick={handleSubmit}
      >
        Save
      </Button>
    </Box>
  );
}
